import Immutable from "immutable";
import _ from "lodash";
import * as Draft from "src/scenes/orgAdmin/formTemplates/types/draft";
import { toAdditionalQuestions, toQuestion } from "src/services/formTemplate";
import * as Question from "src/services/formTemplate/question";
import { flattenQuestion } from "src/services/formTemplate/question";
import { isNotNull } from "src/services/predicates";
import * as AF from "src/types/formTemplate";
import * as GQL from "src/types/graphql";
import { Change, QuestionId, SectionId, State } from "../../types/state";
import {
  hasValidFormQuestionVerification,
  hasValidFormVerification,
  hasValidNewFormVerification,
} from "./utils/typeGuards";

export function toInsertFormVerificationVariables(
  formTemplateId: uuid,
  parentQuestion: Draft.Question
): GQL.InsertFormVerificationsVariables {
  const questions = [
    parentQuestion,
    ...Draft.getAdditionalQuestions(parentQuestion),
  ];

  const flagsByLabel = new Map<
    string,
    { is_avela_ai_assisted: boolean; is_identity_ai_assisted: boolean }
  >();

  for (const question of questions) {
    if (question.newFormVerification === undefined) {
      continue;
    }
    const label = question.newFormVerification.label;
    const next = {
      is_avela_ai_assisted:
        question.newFormVerification.is_avela_ai_assisted ?? false,
      is_identity_ai_assisted:
        question.newFormVerification.is_identity_ai_assisted ?? false,
    };
    const existing = flagsByLabel.get(label);
    if (!existing) {
      flagsByLabel.set(label, next);
    } else {
      flagsByLabel.set(label, {
        is_avela_ai_assisted:
          existing.is_avela_ai_assisted || next.is_avela_ai_assisted,
        is_identity_ai_assisted:
          existing.is_identity_ai_assisted || next.is_identity_ai_assisted,
      });
    }
  }

  const inserts: GQL.form_verification_insert_input[] = Array.from(
    flagsByLabel.entries()
  ).map(([label, flags]) => ({
    form_template_id: formTemplateId,
    label,
    is_avela_ai_assisted: flags.is_avela_ai_assisted,
    is_identity_ai_assisted: flags.is_identity_ai_assisted,
  }));

  return { inserts };
}

export function toUpdateVariables(
  customQuestionTypes: GQL.GetQuestionTypesByOrganization_custom_question_type[],
  enrollmentPeriodId: uuid,
  formTemplateId: uuid,
  state: State
): GQL.UpdateQuestionsAndSectionsVariables {
  const disclaimers: Immutable.Map<
    SectionId,
    AF.DisclaimerSection<AF.WithId>
  > = getAllDrafts(state.get("disclaimers"));

  const sections: Immutable.Map<
    SectionId,
    AF.Section<AF.WithId>
  > = getAllDrafts(state.get("sections"));

  const { sectionUpdates, schoolRankingUpdates } = toSectionUpdates(sections);

  const { sectionInserts, sectionUpdates: sectionOrderUpdates } =
    insertNewSections(
      customQuestionTypes,
      formTemplateId,
      enrollmentPeriodId,
      state
    );

  const { questionUpdates } = toQuestionUpdates(state);

  const { gradesQuestionUpdates } = toGradesQuestionFiltersUpdates(state);

  const {
    eligibilityQuestionSchoolsDeletes,
    eligibilityQuestionSchoolsInserts,
    formQuestionUpdates: formCategoryUpdates,
  } = toEligibilityUpdates(state);

  const disclaimerUpdates = toDisclaimerUpdates(disclaimers);

  const formVerificationUpdates = toFormVerificationUpdates(state);
  const formFiltersUpdates = toFormFiltersUpdates(state);

  const questionOptionInserts = toQuestionOptionInserts(
    enrollmentPeriodId,
    state,
    formTemplateId
  );

  const questionOptionUpdates = toQuestionOptionUpdates(state);

  const { questionInserts, questionUpdates: questionOrderUpdates } =
    insertNewQuestions(
      customQuestionTypes,
      enrollmentPeriodId,
      formTemplateId,
      state
    );

  const {
    additionalQuestionInserts,
    questionUpdates: additionalQuestionUpdates,
  } = toAdditionalQuestionInserts(enrollmentPeriodId, state, formTemplateId);

  return {
    questionUpdates: [
      questionUpdates,
      questionOrderUpdates,
      additionalQuestionUpdates,
      formVerificationUpdates,
    ]
      .filter(isNotNull)
      .flat(),
    disclaimerUpdates,
    schoolRankingUpdates: schoolRankingUpdates?.filter(isNotNull) ?? [],
    formQuestionUpdates: [formCategoryUpdates, formFiltersUpdates]
      .filter(isNotNull)
      .flat(),
    eligibilityQuestionSchoolsDeletes,
    eligibilityQuestionSchoolsInserts,
    questionOptionInserts,
    questionOptionUpdates,
    questionInserts,
    sectionUpdates: [sectionUpdates, sectionOrderUpdates?.reverse()]
      .filter(isNotNull)
      .flat(),
    additionalQuestionInserts,
    gradesQuestionUpdates,
    sectionInserts,
  };
}

type AdditionalQuestions = Pick<
  GQL.UpdateQuestionsAndSectionsVariables,
  "additionalQuestionInserts" | "questionUpdates"
>;
function toAdditionalQuestionInserts(
  enrollmentPeriodId: uuid,
  state: State,
  formTemplateId: uuid
): AdditionalQuestions {
  const questions = state.get("questions");
  const drafts = getAllDrafts(questions);
  const options = drafts
    .valueSeq()
    .toArray()
    .flatMap((draft) => (Question.hasOptions(draft) ? draft.options : []));
  const { additionalQuestionInserts, questionUpdates } = options.reduce(
    (acc: AdditionalQuestions, option) => {
      if (option.additionalQuestions === undefined) {
        return acc;
      }

      if (Draft.isNew(option)) {
        return acc;
      }

      return option.additionalQuestions.reduce((acc, question, order) => {
        if (!Draft.isNew(question)) {
          // update order number of existing additional questions
          const update: GQL.question_updates = {
            where: {
              _and: [{ id: { _eq: question.id } }, { order: { _neq: order } }],
            },
            _set: { order },
          };
          return {
            ...acc,
            questionUpdates: [...(acc.questionUpdates ?? []), update],
          };
        }

        return {
          questionUpdates: acc.questionUpdates,
          additionalQuestionInserts: [
            ...(acc.additionalQuestionInserts ?? []),
            {
              form_question_option_id: option.id,
              question: {
                data: toQuestion(
                  [],
                  enrollmentPeriodId,
                  Draft.toQuestionWithoutId(
                    Draft.fromOriginalQuestion(question)
                  ),
                  order,
                  formTemplateId
                ),
              },
            },
          ],
        };
      }, acc);
    },
    {
      additionalQuestionInserts: [],
      questionUpdates: [],
    } as AdditionalQuestions
  );

  // TODO update order number of existing additional questions
  return {
    additionalQuestionInserts,
    questionUpdates,
  };
}

export function insertNewQuestions(
  customQuestionTypes: GQL.GetQuestionTypesByOrganization_custom_question_type[],
  enrollmentPeriodId: uuid,
  formTemplateId: uuid,
  state: State
): Pick<
  GQL.UpdateQuestionsAndSectionsVariables,
  "questionInserts" | "questionUpdates"
> {
  const [inserts, updates] = state.get("sortedQuestions").reduce(
    ([inserts, updates], questionIds, sectionKey) => {
      // Had to do this to avoid adding duplicate new questions, if a question belongs to a new section, will be added together later.
      const isNewSection = state.get("newSections").has(sectionKey);

      if (isNewSection) {
        return [inserts, updates];
      }

      const [newQuestions, questionsOrderUpdates] = toNewQuestionsInserts(
        customQuestionTypes,
        enrollmentPeriodId,
        formTemplateId,
        sectionKey,
        questionIds,
        state
      );

      if (newQuestions.size === 0) {
        // no new questions, don't do anything
        return [inserts, updates];
      }

      return [
        inserts.concat(newQuestions),
        updates.concat(questionsOrderUpdates.reverse()),
      ];
    },
    [
      Immutable.List<GQL.question_insert_input>(),
      Immutable.List<GQL.question_updates>(),
    ] as const
  );

  return {
    questionInserts: inserts.toArray(),
    questionUpdates: updates.toArray(),
  };
}

function toNewQuestionsInserts(
  customQuestionTypes: GQL.GetQuestionTypesByOrganization_custom_question_type[],
  enrollmentPeriodId: uuid,
  formTemplateId: uuid,
  sectionKey: SectionId,
  questionIds: Immutable.List<QuestionId>,
  state: State
): readonly [
  Immutable.List<GQL.question_insert_input>,
  Immutable.List<GQL.question_updates>
] {
  const newQuestionsMap = state.get("newQuestions");

  return questionIds.reduce(
    ([inserts, updates], questionId, order) => {
      const newQuestion = newQuestionsMap.get(questionId);
      if (!newQuestion) {
        // not a new question
        return [
          inserts,
          updates.push({
            where: {
              _and: [
                { id: { _eq: questionId.get("questionId") } },
                { order: { _neq: order } },
              ],
            },
            _set: { order: order },
          }),
        ];
      }

      const questionWithoutId = Draft.toQuestionWithoutId(newQuestion);

      return [
        inserts.push({
          ...toQuestion(
            customQuestionTypes,
            enrollmentPeriodId,
            questionWithoutId,
            order,
            formTemplateId
          ),
          visibility: newQuestion.visibility ?? null,
          form_template_section_id: sectionKey.get("sectionId"),
        }),
        updates,
      ];
    },
    [
      Immutable.List<GQL.question_insert_input>(),
      Immutable.List<GQL.question_updates>(),
    ] as const
  );
}

function toQuestionOptionUpdates(
  state: State
): GQL.form_question_option_updates[] {
  const drafts = getAllDraftQuestions(state);
  const originals = getAllOriginalQuestions(state);
  const allOriginalOptions = getAllOptions(originals);
  return drafts.flatMap((draft) => {
    if (!Question.hasOptions(draft)) {
      return [];
    }

    return draft.options.flatMap(
      (draftOption): GQL.form_question_option_updates[] => {
        const originalOption = allOriginalOptions.get(draftOption.id);
        if (Draft.isNew(draftOption)) {
          return [];
        }

        if (
          originalOption?.label === draftOption.label &&
          originalOption?.value === draftOption.value
        ) {
          return [];
        }

        return [
          {
            where: {
              id: { _eq: draftOption.id },
            },
            _set: {
              label: draftOption.label,
              value: draftOption.value,
            },
          },
        ];
      }
    );
  });
}

function getAllOptions(
  questions: AF.Question<AF.WithId>[]
): Immutable.Map<uuid, AF.Option<AF.WithId>> {
  const options: [uuid, AF.Option<AF.WithId>][] = questions.flatMap((q) => {
    if (!Question.hasOptions(q)) {
      return [];
    }

    const nestedOptions: AF.Option<AF.WithId>[] = q.options.flatMap(
      (o) =>
        o.additionalQuestions?.flatMap((nestedQuestion) => {
          if (!Question.hasOptions(nestedQuestion)) {
            return [];
          }

          return nestedQuestion.options;
        }) ?? []
    );

    const keyValues: [uuid, AF.Option<AF.WithId>][] = q.options
      .concat(nestedOptions)
      .map((o) => [o.id, o]);
    return keyValues;
  });

  return Immutable.Map<uuid, AF.Option<AF.WithId>>(options);
}

function toQuestionOptionInserts(
  enrollmentPeriodId: uuid,
  state: State,
  formTemplateId: uuid
): GQL.form_question_option_insert_input[] {
  const questions = getAllDraftQuestions(state);
  return questions.flatMap((draft) => {
    if (!Question.hasOptions(draft)) {
      return [];
    }

    return draft.options.flatMap(
      (option, order): GQL.form_question_option_insert_input[] => {
        if (!Draft.isNew(option)) {
          return [];
        }

        if (Draft.isNew(draft)) {
          return [];
        }

        return [
          {
            label: option.label,
            order: order,
            question_id: draft.id,
            value: option.value,
            eligibility_schools: toEligibilityInserts(option),
            additional_questions: toNewAdditionalQuestions(
              enrollmentPeriodId,
              option,
              formTemplateId
            ),
          },
        ];
      }
    );
  });
}

function toNewAdditionalQuestions(
  enrollmentPeriodId: uuid,
  option: Draft.Option,
  formTemplateId: uuid
): GQL.additional_question_arr_rel_insert_input | null {
  if (
    option.additionalQuestions === undefined ||
    option.additionalQuestions.length === 0
  ) {
    return null;
  }

  return toAdditionalQuestions(
    enrollmentPeriodId,
    option.additionalQuestions.map((q) =>
      Draft.toQuestionWithoutId(Draft.fromOriginalQuestion(q))
    ),
    formTemplateId
  );
}

function toEligibilityInserts(
  option: Draft.Option
): GQL.eligibility_question_school_arr_rel_insert_input {
  switch (option.eligibilityFilter) {
    case "NotApplicable":
    case undefined:
      return { data: [] };

    case "Eligible":
      return {
        data: option.eligibleSchoolIds.map((schoolId) => {
          return {
            is_eligible: true,
            school_id: schoolId,
          };
        }),
      };

    case "NotEligible":
      return {
        data: option.notEligibleSchoolIds.map((schoolId) => {
          return {
            is_eligible: false,
            school_id: schoolId,
          };
        }),
      };

    default:
      const _exhaustiveCheck: never = option;
      return _exhaustiveCheck;
  }
}

function toDisclaimerUpdates(
  disclaimers: Immutable.Map<SectionId, AF.DisclaimerSection<AF.WithId>>
): GQL.disclaimer_section_updates[] {
  return disclaimers.toArray().map(([_, disclaimer]) => {
    return {
      where: { id: { _eq: disclaimer.id } },
      _set: { disclaimer: disclaimer.disclaimer },
    };
  });
}

function toSectionUpdates(
  sections: Immutable.Map<SectionId, AF.Section<AF.WithId>>
) {
  const sectionUpdates: GQL.form_template_section_updates[] = [];
  const schoolRankingUpdates: GQL.schools_ranking_section_updates[] = [];

  sections.toArray().forEach(([_, section]) => {
    let sectionId = section.id;
    if (section.type === AF.DisclaimerSectionType) {
      sectionId = section.disclaimerFormSectionId;
    } else if (section.type === AF.SchoolRankingSectionType) {
      sectionId = section.schoolRankingFormSectionId;
      schoolRankingUpdates.push({
        where: { id: { _eq: section.id } },
        _set: {
          min_schools: section.minSchools || null,
          max_schools: section.maxSchools || null,
          explore_url: section.exploreUrl || null,
          ranking_enabled: section.rankingEnabled,
        },
      });
    }

    sectionUpdates.push({
      where: { id: { _eq: sectionId } },
      _set: {
        title: section.title,
        description: section.description,
        key: section.key?.trim() || null,
        permission_level: section.permissionLevel || null,
        family_editable_statuses: section.familyEditableStatuses || null,
      },
    });
  });

  return { sectionUpdates, schoolRankingUpdates };
}

export function insertNewSections(
  customQuestionTypes: GQL.GetQuestionTypesByOrganization_custom_question_type[],
  formTemplateId: uuid,
  enrollmentPeriodId: uuid,
  state: State
): Pick<
  GQL.UpdateQuestionsAndSectionsVariables,
  "sectionInserts" | "sectionUpdates"
> {
  const sections = state.get("sections");
  const sortedSections = state.get("sortedSections");
  const newSectionsMap = state.get("newSections");
  const [inserts, updates] = sortedSections.reduce(
    ([inserts, updates], sectionId, order) => {
      if (newSectionsMap.size === 0) {
        // no new sections, don't do anything
        return [inserts, updates];
      }
      const newSection = newSectionsMap.get(sectionId);

      if (!newSection) {
        // not a new section
        const existingSection = sections.get(sectionId);
        if (!existingSection) {
          return [inserts, updates];
        }
        if (
          existingSection?.original.type ===
          GQL.form_template_section_type_enum.SchoolRankingSection
        ) {
          return [
            inserts,
            updates.push({
              where: {
                _and: [
                  {
                    schools_ranking_section: {
                      id: { _eq: sectionId.get("sectionId") },
                    },
                  },
                  { order: { _neq: order } },
                ],
              },
              _set: { order: order },
            }),
          ];
        }
        if (
          existingSection?.original.type ===
          GQL.form_template_section_type_enum.DisclaimerSection
        ) {
          return [
            inserts,
            updates.push({
              where: {
                _and: [
                  {
                    disclaimer_section: {
                      id: { _eq: sectionId.get("sectionId") },
                    },
                  },
                  { order: { _neq: order } },
                ],
              },
              _set: { order: order },
            }),
          ];
        }
        return [
          inserts,
          updates.push({
            where: {
              _and: [
                { id: { _eq: sectionId.get("sectionId") } },
                { order: { _neq: order } },
              ],
            },
            _set: { order: order },
          }),
        ];
      }

      switch (newSection.type) {
        case "SchoolRankingSection":
          return [
            inserts.push({
              title: newSection.title,
              description: newSection.description,
              type: newSection.type as GQL.form_template_section_type_enum,
              key: newSection.key?.trim() || null,
              order: order,
              form_template_id: formTemplateId,
              permission_level: newSection.permissionLevel || null,
              family_editable_statuses:
                newSection.familyEditableStatuses || null,
              schools_ranking_section: {
                data: {
                  min_schools: newSection.minSchools || null,
                  max_schools: newSection.maxSchools || null,
                  explore_url: newSection.exploreUrl || null,
                  ranking_enabled: newSection.rankingEnabled,
                },
              },
            }),
            updates,
          ];

        case "DisclaimerSection":
          return [
            inserts.push({
              title: newSection.title,
              description: newSection.description,
              type: newSection.type as GQL.form_template_section_type_enum,
              key: newSection.key?.trim() || null,
              order: order,
              form_template_id: formTemplateId,
              permission_level: newSection.permissionLevel || null,
              family_editable_statuses:
                newSection.familyEditableStatuses || null,
              disclaimer_section: {
                data: {
                  disclaimer: newSection.disclaimer,
                },
              },
            }),
            updates,
          ];

        default: {
          const questionsRelatedToSection = state
            .get("sortedQuestions")
            .get(sectionId);

          const newQuestions = state.get("newQuestions");

          const questions = questionsRelatedToSection
            ?.map((questionId, index) => {
              const newQuestion = newQuestions.get(questionId);

              if (newQuestion) {
                const questionWithoutId =
                  Draft.toQuestionWithoutId(newQuestion);

                newQuestions.remove(questionId);
                return {
                  ...toQuestion(
                    customQuestionTypes,
                    enrollmentPeriodId,
                    questionWithoutId,
                    index,
                    formTemplateId
                  ),
                };
              }
              return null;
            })
            .filter(isNotNull);

          return [
            inserts.push({
              title: newSection.title,
              description: newSection.description,
              type: newSection.type as GQL.form_template_section_type_enum,
              key: newSection.key?.trim() || null,
              order: order,
              questions: { data: questions?.toArray() ?? [] },
              form_template_id: formTemplateId,
              permission_level: newSection.permissionLevel || null,
              family_editable_statuses:
                newSection.familyEditableStatuses || null,
            }),
            updates,
          ];
        }
      }
    },
    [
      Immutable.List<GQL.form_template_section_insert_input>(),
      Immutable.List<GQL.form_template_section_updates>(),
    ] as const
  );

  return {
    sectionInserts: inserts.toArray(),
    sectionUpdates: updates.toArray(),
  };
}

function toQuestionUpdates(
  state: State
): GQL.UpdateQuestionsAndSectionsVariables {
  const questions = getAllDraftQuestions(state);
  return {
    questionUpdates: questions.map((question) => {
      return {
        where: { id: { _eq: question.id } },
        _set: {
          question: question.question,
          key: question.key ?? null,
          link_text: question.link_text ?? null,
          link_url: question.link_url ?? null,
          permission_level: question.permissionLevel ?? null,
          is_required: question.requirement === "Required",
          constraints: question.constraints ?? null,
          visibility: question.visibility ?? null,
        },
      };
    }),
  };
}

function toGradesQuestionFiltersUpdates(
  state: State
): GQL.UpdateQuestionsAndSectionsVariables {
  const questions = getAllDraftQuestions(state).filter(
    (question) => question.filters
  );
  return {
    gradesQuestionUpdates: questions.map((question) => {
      return {
        where: { question_id: { _eq: question.id } },
        _set: {
          filters: question.filters,
        },
      };
    }),
  };
}

function toFormVerificationUpdates(state: State): GQL.question_updates[] {
  const questionChanges = state.get("questions");
  const questions = getAllDraftQuestions(state);
  return questions.flatMap((question) => {
    const original = questionChanges.get(QuestionId(question.id))?.original;
    const formVerificationId = question.formVerification?.id ?? null;

    const update: GQL.question_updates[] = [
      {
        where: { id: { _eq: question.id } },
        _set: {
          form_verification_id: formVerificationId,
        },
      },
    ];

    if (!original) {
      return update;
    }

    if (original.formVerification?.id === formVerificationId) {
      return [];
    }

    return update;
  });
}

/**
 * Extract form verification records that need AI assist flags updated
 * (Avela Intelligence + Identity AI checkboxes in the verification UI).
 *
 * Uses `false` when a flag was not resolved from draft snapshots (e.g. org-level
 * config hides a checkbox): we do not preserve the previous DB value in that case.
 */

export function getVerificationAIAssistUpdates(state: State): Array<{
  id: string;
  is_avela_ai_assisted: boolean;
  is_identity_ai_assisted: boolean;
}> {
  // Get all draft questions from state
  const questions = getAllDraftQuestions(state);

  // First identify all unique verification IDs and their latest state
  // Map key is verification ID, value is an object with verification info and question references
  type VerificationInfo = {
    isAvelaAiAssisted?: boolean;
    isIdentityAiAssisted?: boolean;
    explicitlyModified: boolean; // Whether this has been explicitly set by the user
    fromDraft: boolean; // Is this from a draft (edited) question
    questionIds: Set<string>; // All questions that reference this verification
  };

  const verificationMap = new Map<string, VerificationInfo>();

  // Process each question to find verifications and track their state
  for (const question of questions) {
    // Skip questions without an ID
    if (!question.id) continue;

    let verificationId: string | undefined;
    let isAvelaAiAssisted: boolean | undefined;
    let isIdentityAiAssisted: boolean | undefined;
    let fromDraft = false;
    let explicitlyModified = false;

    // Use our type guards for cleaner code

    // CASE 1: Check for formVerification (from newly edited questions)
    if (hasValidFormVerification(question)) {
      verificationId = question.formVerification.id;

      if ("is_avela_ai_assisted" in question.formVerification) {
        isAvelaAiAssisted = question.formVerification.is_avela_ai_assisted;
        fromDraft = true;
        explicitlyModified = true; // Changes in formVerification are explicit user edits
      }
      if ("is_identity_ai_assisted" in question.formVerification) {
        isIdentityAiAssisted =
          question.formVerification.is_identity_ai_assisted;
        fromDraft = true;
        explicitlyModified = true;
      }
    }
    // CASE 2: Check for newFormVerification (for newly created verifications)
    else if (hasValidNewFormVerification(question)) {
      // New verifications get a temporary 'NEW' ID and won't be shared
      continue; // Skip - new verifications will be handled separately
    }
    // CASE 3: Check for form_question.form_verification (from database)
    else if (hasValidFormQuestionVerification(question)) {
      // We know form_verification exists and has an id because of the type guard
      // but TypeScript still needs additional safety checks
      const formVerification = question.form_question.form_verification;
      if (formVerification && formVerification.id) {
        verificationId = formVerification.id;

        if ("is_avela_ai_assisted" in formVerification) {
          isAvelaAiAssisted = formVerification.is_avela_ai_assisted;
        }
        if ("is_identity_ai_assisted" in formVerification) {
          isIdentityAiAssisted = formVerification.is_identity_ai_assisted;
        }
      }
    }

    // If we found a verification ID, add it to our map
    if (verificationId && verificationId !== "NEW" && question.id) {
      // Get or initialize the verification info
      const existingInfo = verificationMap.get(verificationId) || {
        isAvelaAiAssisted: undefined,
        isIdentityAiAssisted: undefined,
        explicitlyModified: false,
        fromDraft: false,
        questionIds: new Set<string>(),
      };

      // Update the verification info with this question's data
      if (
        isAvelaAiAssisted !== undefined ||
        isIdentityAiAssisted !== undefined
      ) {
        // Only update if not already set by a draft (which takes precedence)
        if (!existingInfo.explicitlyModified || fromDraft) {
          if (isAvelaAiAssisted !== undefined) {
            existingInfo.isAvelaAiAssisted = isAvelaAiAssisted;
          }
          if (isIdentityAiAssisted !== undefined) {
            existingInfo.isIdentityAiAssisted = isIdentityAiAssisted;
          }
          existingInfo.explicitlyModified =
            existingInfo.explicitlyModified || explicitlyModified;
          existingInfo.fromDraft = existingInfo.fromDraft || fromDraft;
        }
      }

      // Add this question ID to the set
      existingInfo.questionIds.add(question.id);

      // Update the map
      verificationMap.set(verificationId, existingInfo);
    }
  }

  return Array.from(verificationMap.entries())
    .filter(
      ([_, info]) =>
        info.isAvelaAiAssisted !== undefined ||
        info.isIdentityAiAssisted !== undefined
    )
    .map(([verificationId, info]) => ({
      id: verificationId,
      is_avela_ai_assisted: info.isAvelaAiAssisted ?? false,
      is_identity_ai_assisted: info.isIdentityAiAssisted ?? false,
    }));
}

// NOTE: Type guards moved to ./utils/typeGuards.ts for better code organization

function toFormFiltersUpdates(state: State): GQL.form_question_updates[] {
  const questionChanges = state.get("questions");
  const questions = getAllDraftQuestions(state);
  return questions.flatMap((question): GQL.form_question_updates[] => {
    const original = questionChanges.get(QuestionId(question.id))?.original;
    switch (question.type) {
      case AF.DateType:
      case AF.NumberType:
        return [];
      case AF.GradesType:
      case AF.CustomQuestionType:
        return [];

      case AF.EmailType:
      case AF.SingleSelectType:
      case AF.MultiSelectType:
      case AF.AddressType:
      case AF.FreeTextType:
      case AF.FileUploadType:
      case AF.PhoneNumberType:
        const update: GQL.form_question_updates[] = [
          {
            where: { question_id: { _eq: question.id } },
            _set: {
              filters: question.filters,
            },
          },
        ];

        if (_.isEqual(original?.filters, question.filters)) {
          return [];
        }

        return update;

      default:
        const _exhaustiveCheck: never = question;
        return _exhaustiveCheck;
    }
  });
}

function getAllDrafts<T, U, K>(
  changes: Immutable.Map<K, Change<T, U>>
): Immutable.Map<K, U> {
  return changes.flatMap((change: Change<T, U>, key: K): [K, U][] => {
    return change.draft ? [[key, change.draft]] : [];
  });
}

function getAllDraftQuestions(state: State): Draft.Question[] {
  // we can potentially memoize this function for performance improvement when necessary
  const drafts: Draft.Question[] = state
    .get("questions")
    .toArray()
    .flatMap(([_, questionChange]) => {
      return questionChange.draft
        ? flattenQuestion(questionChange.draft).map(Draft.fromOriginalQuestion)
        : [];
    });
  return drafts;
}

function getAllOriginalQuestions(state: State): AF.Question<AF.WithId>[] {
  // we can potentially memoize this function for performance improvement when necessary
  const originalQuestions: AF.Question<AF.WithId>[] = state
    .get("questions")
    .toArray()
    .flatMap(([_, questionChange]) => {
      return questionChange.original
        ? flattenQuestion(questionChange.original)
        : [];
    });

  return originalQuestions;
}

function hasEligiblity(draft: Draft.Question): boolean {
  if (!Question.hasOptions(draft)) {
    return false;
  }

  return !draft.options.every(
    (option) =>
      option.eligibilityFilter === undefined ||
      option.eligibilityFilter === "NotApplicable"
  );
}

export function toEligibilityUpdates(
  state: State
): GQL.UpdateQuestionsAndSectionsVariables {
  const originalQuestions = getAllOriginalQuestions(state);
  const questions = getAllDraftQuestions(state);
  const originalQuestionMap = Immutable.Map(
    originalQuestions.map((q) => [QuestionId(q.id), q])
  );

  const originalEligibilities = getAllEligibilities(originalQuestions);
  const draftEligibilities = getAllEligibilities(questions);

  // update category to be "Eligible" or "General"
  const formQuestionUpdates: GQL.form_question_updates[] = questions.flatMap(
    (draft) => {
      const original = originalQuestionMap.get(QuestionId(draft.id));
      if (Draft.isNew(draft)) {
        // new question, don't update
        return [];
      }
      if (!Draft.hasChanges(original, draft)) {
        // no changes, don't update category
        return [];
      }

      return [
        {
          where: { question_id: { _eq: draft.id } },
          _set: {
            category: hasEligiblity(draft)
              ? GQL.form_question_category_enum.EligibilityQuestion
              : GQL.form_question_category_enum.GeneralQuestion,
          },
        },
      ];
    }
  );

  // go through original eligibilies to find the one need to be deleted
  const deletes: GQL.eligibility_question_school_bool_exp[] =
    originalEligibilities.reduce(
      (
        currentDeletes: GQL.eligibility_question_school_bool_exp[],
        original: EligibilityOption
      ) => {
        const draft = draftEligibilities.get(original.optionId);

        const questionInDraft = questions.some(
          (q) => q.id === original.questionId
        );
        if (!questionInDraft) {
          // question for this option is not in drafts, don't delete.
          return currentDeletes;
        }

        if (Draft.isNew(draft)) {
          // don't delete new question
          return currentDeletes;
        }
        if (draft && _.isEqual(draft, original)) {
          // draft is the same as original, we want to keep this
          return currentDeletes;
        }

        // draft is not the same, we should delete the original
        return [
          ...currentDeletes,
          { form_question_option_id: { _eq: original.optionId } },
        ];
      },
      []
    );

  // go through draft to generate inserts
  const inserts: GQL.eligibility_question_school_insert_input[] =
    draftEligibilities.reduce(
      (
        currentInserts: GQL.eligibility_question_school_insert_input[],
        draft: EligibilityOption
      ) => {
        if (Draft.isNew(draft)) {
          // new opton, don't insert since this will inserted through option relationship insert already.
          return currentInserts;
        }
        const original = originalEligibilities.get(draft.optionId);
        if (original && _.isEqual(original, draft)) {
          // draft is the same as equal, don't insert this
          return currentInserts;
        }

        // draft is new or different from original, we should insert this
        return currentInserts.concat(
          draft.eligibility.eligibilityFilter === "Eligible"
            ? draft.eligibility.eligibleSchoolIds.map((schoolId) => ({
                form_question_option_id: draft.optionId,
                school_id: schoolId,
                is_eligible: true,
              }))
            : draft.eligibility.notEligibleSchoolIds.map((schoolId) => ({
                form_question_option_id: draft.optionId,
                school_id: schoolId,
                is_eligible: false,
              }))
        );
      },
      []
    );

  return {
    eligibilityQuestionSchoolsDeletes:
      deletes.length > 0 ? { _or: deletes } : undefined,
    eligibilityQuestionSchoolsInserts: inserts,
    formQuestionUpdates,
  };
}

type EligibilityOption = {
  questionId: uuid;
  optionId: uuid;
  eligibility: AF.Eligible | AF.NotEligible;
  isNew: boolean;
};
export function getAllEligibilities(
  questions: AF.Question<AF.WithId>[]
): Immutable.Map<uuid, EligibilityOption> {
  const keyValues = questions.flatMap((q) => {
    if (!Question.hasOptions(q)) {
      return [];
    }

    return q.options.flatMap((o): [uuid, EligibilityOption][] => {
      switch (o.eligibilityFilter) {
        case "NotApplicable":
        case undefined:
          return [];

        case "Eligible":
          return [
            [
              o.id,
              {
                questionId: q.id,
                optionId: o.id,
                eligibility: {
                  eligibilityFilter: "Eligible",
                  eligibleSchoolIds: o.eligibleSchoolIds,
                },
                isNew: Draft.isNew(o),
              },
            ],
          ];

        case "NotEligible":
          return [
            [
              o.id,
              {
                questionId: q.id,
                optionId: o.id,
                eligibility: {
                  eligibilityFilter: "NotEligible",
                  notEligibleSchoolIds: o.notEligibleSchoolIds,
                },
                isNew: Draft.isNew(o),
              },
            ],
          ];

        default:
          const _exhaustiveCheck: never = o;
          return _exhaustiveCheck;
      }
    });
  });

  return Immutable.Map(keyValues);
}

/**
 * Section rules
 */
export const ALL_SECTION_TYPES: AF.SectionType[] = [
  AF.PreRankingSectionType,
  AF.SchoolRankingSectionType,
  AF.GeneralSectionType,
  AF.DisclaimerSectionType,
];

export const getApplicableSectionTypes = (
  order: number,
  sections: AF.Section<AF.WithId>[],
  section?: AF.Section<AF.WithId>
): [AF.SectionType[], AF.SectionType[]] => {
  const {
    hasPreRankingSection,
    hasSchoolRankingSection,
    hasGeneralSection,
    hasDisclaimerSection,
  } = sections.reduce(
    (acc, curr) => {
      acc.hasPreRankingSection ||= curr.type === "PreRankingSection";
      acc.hasSchoolRankingSection ||= curr.type === "SchoolRankingSection";
      acc.hasGeneralSection ||= curr.type === "GeneralSection";
      acc.hasDisclaimerSection ||= curr.type === "DisclaimerSection";

      return acc;
    },
    {
      hasPreRankingSection: false,
      hasSchoolRankingSection: false,
      hasGeneralSection: false,
      hasDisclaimerSection: false,
    }
  );

  const isLastSection = order === sections.length - 1;

  let before: (AF.SectionType | boolean)[];
  let after: (AF.SectionType | boolean)[];

  if (!section) {
    before = [
      AF.PreRankingSectionType,
      AF.SchoolRankingSectionType,
      AF.GeneralSectionType,
      AF.DisclaimerSectionType,
    ];
    after = [];
  }

  switch (section?.type) {
    case AF.PreRankingSectionType:
      before = [];
      after = [
        !hasSchoolRankingSection && AF.SchoolRankingSectionType,
        !hasSchoolRankingSection && AF.GeneralSectionType,
        isLastSection && !hasDisclaimerSection && AF.DisclaimerSectionType,
      ];
      break;

    case AF.SchoolRankingSectionType:
      before = [!hasPreRankingSection && AF.PreRankingSectionType];
      after = [
        AF.GeneralSectionType,
        isLastSection && !hasDisclaimerSection && AF.DisclaimerSectionType,
      ];
      break;

    case AF.GeneralSectionType:
      before = [
        !hasPreRankingSection && AF.PreRankingSectionType,
        !hasSchoolRankingSection && AF.SchoolRankingSectionType,
      ];
      after = [
        AF.GeneralSectionType,
        isLastSection && !hasDisclaimerSection && AF.DisclaimerSectionType,
      ];
      break;

    case AF.DisclaimerSectionType:
      before = [
        !hasGeneralSection && !hasPreRankingSection && AF.PreRankingSectionType,
        !hasGeneralSection &&
          !hasSchoolRankingSection &&
          AF.SchoolRankingSectionType,
        AF.GeneralSectionType,
      ];
      after = [];
      break;

    default:
      before = ALL_SECTION_TYPES;
      after = [];
  }
  return [
    order === 0 ? (before.filter(Boolean) as AF.SectionType[]) : [],
    after.filter(Boolean) as AF.SectionType[],
  ];
};
